import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatPrice, resolveImg } from "@/lib/format";
import { Loader2, Package, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-aak-accent text-aak-bg",
  completed: "bg-aak-accent text-aak-bg",
  pending: "bg-aak-muted text-aak-mutedfg",
  initiated: "bg-aak-muted text-aak-mutedfg",
  failed: "bg-red-100 text-red-700",
  expired: "bg-red-100 text-red-700",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export const AdminOrders = ({ authHeader }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders", { headers: authHeader() });
      setOrders(data);
    } catch {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const setFulfillment = async (sid, status) => {
    try {
      await api.patch(`/admin/orders/${sid}/status`, { status }, { headers: authHeader() });
      setOrders((prev) => prev.map((o) => (o.session_id === sid ? { ...o, fulfillment_status: status } : o)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const FULFILL = ["new", "packed", "shipped", "delivered"];

  const paidCount = orders.filter((o) => o.payment_status === "paid").length;
  const revenue = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + (o.amount || 0), 0);

  if (loading) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-aak-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-orders">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl text-aak-fg tracking-tight">Orders</h1>
          <p className="text-sm text-aak-mutedfg mt-2">
            {orders.length} orders · {paidCount} paid · {formatPrice(revenue)} collected
          </p>
        </div>
        <button
          data-testid="refresh-orders-btn"
          onClick={load}
          className="flex items-center gap-2 border border-aak-border px-5 py-3 text-xs uppercase tracking-[0.2em] text-aak-fg hover:border-aak-fg"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="border border-aak-border bg-aak-surface/40 p-16 text-center">
          <Package className="w-10 h-10 mx-auto text-aak-mutedfg mb-4" strokeWidth={1.3} />
          <p className="text-aak-mutedfg">No orders yet. They'll appear here after checkout.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const cust = o.customer || {};
            const badge = STATUS_STYLES[o.payment_status] || STATUS_STYLES.pending;
            return (
              <div key={o.session_id} data-testid={`order-${o.session_id}`} className="border border-aak-border bg-aak-surface/40">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-aak-border">
                  <div>
                    <p className="font-serif text-xl text-aak-fg leading-none">{cust.name || "Guest"}</p>
                    <p className="text-xs text-aak-mutedfg mt-1">{fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${badge}`}>
                      {o.payment_status}
                    </span>
                    <span className="font-serif text-xl text-aak-fg">{formatPrice(o.amount || 0)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-5">
                  <div className="md:col-span-1 text-sm space-y-1">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-aak-primary mb-2">Customer</p>
                    {cust.email && <p className="text-aak-fg break-all">{cust.email}</p>}
                    {cust.phone && <p className="text-aak-mutedfg">{cust.phone}</p>}
                    {cust.address && <p className="text-aak-mutedfg whitespace-pre-line">{cust.address}</p>}
                    {!cust.email && !cust.address && <p className="text-aak-mutedfg">—</p>}
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-aak-primary mb-3">Items</p>
                    <div className="space-y-3">
                      {(o.items || []).map((it, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-12 h-16 bg-aak-muted overflow-hidden flex-shrink-0">
                            {it.image && <img src={resolveImg(it.image)} alt={it.name} className="w-full h-full object-cover object-top" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-serif text-lg text-aak-fg truncate">{it.name || it.id}</p>
                            <p className="text-xs text-aak-mutedfg">
                              Size {it.size || "—"} · Qty {it.quantity}
                            </p>
                          </div>
                          {it.price != null && (
                            <span className="text-sm text-aak-mutedfg">{formatPrice(it.price * it.quantity)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-aak-border bg-aak-bg/40">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-aak-mutedfg mr-1">Fulfillment</span>
                  {FULFILL.map((s) => (
                    <button
                      key={s}
                      data-testid={`fulfill-${s}-${o.session_id}`}
                      onClick={() => setFulfillment(o.session_id, s)}
                      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                        (o.fulfillment_status || "new") === s
                          ? "bg-aak-accent text-aak-bg border-aak-accent"
                          : "border-aak-border text-aak-mutedfg hover:border-aak-fg hover:text-aak-fg"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
